/**
 * Courier Map Controller (sibling-canvas split)
 *
 * Renders a Calisto-branded Leaflet map showing the
 * pickup, dropoff, and live courier positions for a
 * courier delivery in flight. The pickup and
 * dropoff markers are static; the courier marker
 * smoothly animates between successive positions
 * over ~1.5s.
 *
 * The controller element itself is an empty,
 * attribute-only "values" div that lives next to a
 * sibling "canvas" div where Leaflet actually mounts.
 * This split lets us replace the values div via a
 * targeted morph stream (to update the lat/lng data
 * attributes) WITHOUT blowing away Leaflet's tile
 * canvas. The controller looks up the sibling canvas
 * by id (passed via `data-courier-map-canvas-id-value`).
 *
 * Position updates are pushed via targeted Turbo
 * Stream updates that mutate the controller's
 * `data-courier-map-courier-lat-value` /
 * `-lng-value` / `-updated-at-value` attributes.
 * Stimulus's value-changed callbacks fire the
 * animation: no event subscriptions needed here.
 *
 * The map fits its bounds to all three points so
 * the customer always sees the full route while the
 * courier is in flight.
 */
import { Controller } from "@hotwired/stimulus";
import L from "leaflet";
import {
  clearLeafletArtifacts,
  containerHasSize,
  watchContainerSize,
} from "./utils/map_container";
import { pinDivIcon } from "./utils/map_markers";

const ANIMATION_MS = 1500;
const FIT_PADDING = [40, 40];

// Vehicle glyphs for the courier marker (CAL-408).
// Inlined as markup rather than pulled from
// `app/views/components/svgs/` because the marker is
// built here, in JS: Leaflet's `divIcon` takes an HTML
// string, and a Phlex component cannot be rendered into
// one. `currentColor` is what lets them inherit the
// marker's own colour (`text-base-content` since
// CAL-485), so they follow the venue's theme exactly as
// the "C" did.
const SVG_OPEN =
  '<svg xmlns="http://www.w3.org/2000/svg" ' +
  'viewBox="0 0 24 24" fill="none" ' +
  'stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" ' +
  'class="w-5 h-5" aria-hidden="true">';

// Deliberately NOT the bike frame with a dot for a
// rider: rendered at 150px side by side, that reads as
// the bicycle icon, and at the marker's 20px the two
// are the same picture. This silhouette keeps the flat
// tank line and the handlebar, which is what still
// separates it from a bicycle once it is small.
const MOTORCYCLE_SVG =
  SVG_OPEN +
  '<circle cx="18.5" cy="17.5" r="3.5"/>' +
  '<circle cx="5.5" cy="17.5" r="3.5"/>' +
  '<path d="M5.5 17.5h6.5l3.5-6.5"/>' +
  '<path d="M9 11h5l2 3"/>' +
  '<path d="M9 11 7.5 8H5"/>' +
  '<path d="M14.5 8H18"/>' +
  "</svg>";

const CAR_SVG =
  SVG_OPEN +
  '<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7' +
  '-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3' +
  'c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4' +
  '2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/>' +
  '<circle cx="7" cy="17" r="2"/>' +
  '<path d="M9 17h6"/>' +
  '<circle cx="17" cy="17" r="2"/>' +
  "</svg>";

const BICYCLE_SVG =
  SVG_OPEN +
  '<circle cx="18.5" cy="17.5" r="3.5"/>' +
  '<circle cx="5.5" cy="17.5" r="3.5"/>' +
  '<circle cx="15" cy="5" r="1"/>' +
  '<path d="M12 17.5V14l-3-3 4-3 2 3h2"/>' +
  "</svg>";

export default class extends Controller {
  static values = {
    canvasId: String,
    pickupLat: Number,
    pickupLng: Number,
    pickupName: { type: String, default: "" },
    dropoffLat: Number,
    dropoffLng: Number,
    dropoffName: { type: String, default: "" },
    courierLat: Number,
    courierLng: Number,
    courierUpdatedAt: { type: String, default: "" },
    courierName: { type: String, default: "" },
    vehicleType: { type: String, default: "" },
  };

  connect() {
    this.canvasElement = document.getElementById(
      this.canvasIdValue
    );
    if (!this.canvasElement) return;

    // `reconnect_controllers.js` re-runs the lifecycle by
    // hand after a morph, on top of Stimulus's own, so
    // connect() can run twice with no disconnect in
    // between. Leaflet refuses to build a second map on a
    // container it already stamped, and that throw is
    // what stranded a whole Turbo visit in CAL-401. Tear
    // down first so init is idempotent however we got
    // here.
    this.teardownMap();
    this.initializeMap();
  }

  disconnect() {
    this.teardownMap();
  }

  // Returns the canvas to a state `L.map()` accepts, and
  // the controller to a state a fresh connect can start
  // from. `map.remove()` is the happy path, but it throws
  // ("Map container is being reused by another instance")
  // when the canvas no longer carries the id it stamped,
  // and it throws BEFORE cleaning anything up. Swallow it
  // and let `clearLeafletArtifacts` finish, or the next
  // connect meets a stamped canvas and the map is dead
  // for the rest of the session.
  teardownMap() {
    this.cancelAnimation();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    this.viewComputedBlind = false;

    if (this.map) {
      try {
        this.map.remove();
      } catch {
        // Half-dead map: the sweep below still frees
        // the canvas for a re-init.
      }
    }

    this.map = null;
    this.pickupMarker = null;
    this.dropoffMarker = null;
    this.courierMarker = null;

    if (this.canvasElement) {
      clearLeafletArtifacts(this.canvasElement);
    }
  }

  initializeMap() {
    this.map = L.map(this.canvasElement, {
      zoomControl: true,
      scrollWheelZoom: false,
    });

    L.tileLayer(
      "https://{s}.tile.openstreetmap.org/" +
        "{z}/{x}/{y}.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/' +
          'copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }
    ).addTo(this.map);

    this.pickupMarker = L.marker(
      this.pickupLatLng(),
      { icon: this.pickupIcon() }
    )
      .addTo(this.map)
      .bindPopup(
        this.pickupNameValue || "Pickup"
      );

    this.dropoffMarker = L.marker(
      this.dropoffLatLng(),
      { icon: this.dropoffIcon() }
    )
      .addTo(this.map)
      .bindPopup(
        this.dropoffNameValue || "Dropoff"
      );

    if (this.hasCourierPosition()) {
      this.courierMarker = L.marker(
        this.courierLatLng(),
        { icon: this.courierIcon() }
      )
        .addTo(this.map)
        .bindPopup(
          this.courierNameValue || "Courier"
        );
    }

    // A canvas with no box measures 0x0, and the fit
    // below is made against that: Leaflet runs out of
    // zoom levels and stops at the tile layer's max,
    // where a later `invalidateSize()` leaves it: that
    // call re-measures the canvas, never the view.
    // Remember we fitted blind so the reveal can re-fit
    // (CAL-445).
    this.viewComputedBlind = !containerHasSize(
      this.canvasElement
    );

    this.fitToMarkers();

    // Leaflet sometimes mis-measures size when the
    // container is rendered after layout settles.
    setTimeout(() => {
      if (this.map) this.map.invalidateSize();
    }, 100);

    // The canvas, NOT `this.element`: the controller
    // element is the attribute-only values div, which has
    // no box of its own to watch.
    this.resizeObserver = watchContainerSize(
      this.canvasElement,
      () => this.handleContainerResize()
    );
  }

  handleContainerResize() {
    if (!this.map) return;

    this.map.invalidateSize();

    // Once only. Every courier ping re-fits anyway, but a
    // resize is not new data, and re-fitting on one would
    // yank a customer who has panned the map themselves.
    if (this.viewComputedBlind) {
      this.viewComputedBlind = false;
      this.fitToMarkers();
    }
  }

  // Value change callbacks: fire when the targeted
  // Turbo Stream rewrites the data attributes on the
  // values (controller) element.

  courierLatValueChanged() {
    this.handleCourierMoved();
  }

  courierLngValueChanged() {
    this.handleCourierMoved();
  }

  /**
   * The vehicle arrives on its own schedule (CAL-408).
   * The map opens as soon as the provider starts the
   * trip, and the courier (with their vehicle) can
   * be assigned in a webhook that lands after that. So
   * the first marker is often the "C", and this is
   * what swaps the glyph in when the type shows up
   * rather than leaving the letter until the customer
   * reloads.
   *
   * `handleCourierMoved` cannot cover it: the marker
   * already exists by then, so that path animates the
   * position and never rebuilds the icon.
   */
  vehicleTypeValueChanged() {
    if (!this.courierMarker) return;

    this.courierMarker.setIcon(this.courierIcon());
  }

  handleCourierMoved() {
    if (!this.map) return;
    if (!this.hasCourierPosition()) return;

    const next = this.courierLatLng();

    if (!this.courierMarker) {
      this.courierMarker = L.marker(next, {
        icon: this.courierIcon(),
      })
        .addTo(this.map)
        .bindPopup(
          this.courierNameValue || "Courier"
        );
      this.fitToMarkers();
      return;
    }

    const from = this.courierMarker.getLatLng();
    this.animateMarker(this.courierMarker, from, {
      lat: next[0],
      lng: next[1],
    });
    this.fitToMarkers();
  }

  /**
   * Linearly interpolates a Leaflet marker from one
   * lat/lng to another over ANIMATION_MS milliseconds.
   * Cancels any in-flight animation before starting.
   */
  animateMarker(marker, from, to) {
    this.cancelAnimation();

    const start = performance.now();

    const step = (now) => {
      const elapsed = now - start;
      const t = Math.min(elapsed / ANIMATION_MS, 1);

      const lat =
        from.lat + (to.lat - from.lat) * t;
      const lng =
        from.lng + (to.lng - from.lng) * t;
      marker.setLatLng([lat, lng]);

      if (t < 1) {
        this.animationFrame =
          window.requestAnimationFrame(step);
      } else {
        this.animationFrame = null;
      }
    };

    this.animationFrame =
      window.requestAnimationFrame(step);
  }

  cancelAnimation() {
    if (this.animationFrame) {
      window.cancelAnimationFrame(
        this.animationFrame
      );
      this.animationFrame = null;
    }
  }

  fitToMarkers() {
    const points = [
      this.pickupLatLng(),
      this.dropoffLatLng(),
    ];
    if (this.hasCourierPosition()) {
      points.push(this.courierLatLng());
    }
    const bounds = L.latLngBounds(points);
    this.map.fitBounds(bounds, {
      padding: FIT_PADDING,
    });
  }

  hasCourierPosition() {
    return (
      Number.isFinite(this.courierLatValue) &&
      Number.isFinite(this.courierLngValue) &&
      this.courierLatValue !== 0 &&
      this.courierLngValue !== 0
    );
  }

  pickupLatLng() {
    return [
      this.pickupLatValue,
      this.pickupLngValue,
    ];
  }

  dropoffLatLng() {
    return [
      this.dropoffLatValue,
      this.dropoffLngValue,
    ];
  }

  courierLatLng() {
    return [
      this.courierLatValue,
      this.courierLngValue,
    ];
  }

  // The two ends of the run are PLACES, so they are
  // teardrops anchored at the tip: the same pin, and the
  // same two tones, as the route map and the legend
  // badges beside it (CAL-457). Their anchor moves from
  // `[16, 32]` to `[16, 40]`: it used to be the bottom
  // EDGE of a circle, which is not a coordinate.
  pickupIcon() {
    return pinDivIcon({ letter: "P", tone: "primary" });
  }

  // Ink since CAL-485. The order pages wear the
  // merchant's identity the way the checkout does, and
  // the drop-off end of the run was one of the last
  // things on them painted in Calisto's accent. The
  // pickup end stays primary: the two ends have to
  // differ, and primary is the merchant's own.
  dropoffIcon() {
    return pinDivIcon({ letter: "D", tone: "ink" });
  }

  /**
   * The glyph inside the courier marker, chosen from
   * the provider's `vehicle_type` (CAL-408). The card
   * beside this map already names the vehicle in words
   * ("lightskyblue Italika FT150"); a bare "C" on the
   * map said nothing that text did not.
   *
   * Untrusted input: the provider publishes
   * `bicycle | car | van | truck | scooter |
   * motorcycle | walker` today and is free to add to
   * it, so this matches what it knows and lets
   * everything else fall through. Van, truck and
   * walker have no glyph yet and land on the "C":
   * deliberate, and the reason the fallback is a
   * letter rather than a car we would be guessing at.
   */
  courierGlyph() {
    const type = this.vehicleTypeValue
      .trim()
      .toLowerCase();

    if (
      type === "motorcycle" ||
      type === "scooter" ||
      type === "moped"
    ) {
      return MOTORCYCLE_SVG;
    }
    if (type === "car" || type === "auto") {
      return CAR_SVG;
    }
    if (type === "bicycle" || type === "bike") {
      return BICYCLE_SVG;
    }

    return "C";
  }

  courierIcon() {
    return L.divIcon({
      html:
        '<div class="bg-base-100 border-2 ' +
        "border-base-content rounded-full w-10 h-10 " +
        "flex items-center justify-center " +
        "shadow-lg font-bold text-base-content " +
        'text-sm">' +
        this.courierGlyph() +
        "</div>",
      // Unchanged from the "C" marker so swapping the
      // glyph cannot shift the map's layout.
      iconSize: [40, 40],
      iconAnchor: [20, 20],
      popupAnchor: [0, -20],
      className: "",
    });
  }
}
